/**
 * 路由配置与路由守卫
 * 包含静态路由定义、动态路由注册、登录鉴权守卫、进度条控制
 */
import Vue from 'vue';
import VueRouter from 'vue-router';
import store from '@/store';
import setting from '@/config/setting';
import EleLayout from '@/views/common/layout/Layout';
import NProgress from 'nprogress';

Vue.use(VueRouter);

/* 静态路由配置 */
const routes = [
  {
    path: '/login',
    component: () => import('@/views/login/login'),
    meta: { hide: true, title: '登录' },
  },
  {
    path: '/hdLogin',
    component: () => import('@/views/login/hdLogin'),
    meta: { hide: true, title: '登录' },
  },
  {
    path: '/forget',
    component: () => import('@/views/login/forget'),
    meta: { hide: true, title: '忘记密码' },
  },

  {
    path: '/test',
    component: () => import('@/views/test'),
    meta: { hide: true, title: '测试' },
  },
  {
    path: '/test1',
    component: () => import('@/views/test1'),
    meta: { hide: true, title: '测试1' },
  },
  {
    path: '/dorDataV',
    component: () => import('@/views/dorDataV/index'),
    meta: { hide: true, title: '宿舍归寝数据展板' },
  },
  // 嵌套路由需要单独配置，菜单不完全等于路由
  // 以下路由用于静态定义（非动态菜单生成的）多层嵌套页面
  {
    path: '/smsManage',
    component: EleLayout,
    meta: { title: '发送短信' },
    children: [
      {
        path: 'adminSMS',
        redirect: '/smsManage/adminSMS/fromTeacher',
        component: () => import('@/views/smsManage/adminSMS'),
        meta: { title: '校级短信发送' },
        children: [
          {
            path: 'fromGroup',
            component: () => import('@/views/smsManage/adminSMS/fromGroup'),
            meta: {
              hide: true,
              title: '按工作组发送短信',
              uid: '/smsManage/adminSMS',
            },
          },
          {
            path: 'fromTeacher',
            component: () => import('@/views/smsManage/adminSMS/fromTeacher'),
            meta: {
              hide: true,
              title: '按教师发送短信',
              uid: '/smsManage/adminSMS',
            },
          },
        ],
      },
      {
        path: 'teacherSMS',
        redirect: '/smsManage/teacherSMS/fromParent',
        component: () => import('@/views/smsManage/teacherSMS'),
        meta: { title: '家校互动' },
        children: [
          {
            path: 'fromParent',
            component: () => import('@/views/smsManage/teacherSMS/fromParent'),
            meta: {
              hide: true,
              title: '指定学生发送短信',
              uid: '/smsManage/fromParent',
            },
          },
        ],
      },
    ],
  },
  {
    path: '/smsRecord',
    component: EleLayout,
    meta: { title: '短信收发记录' },
    children: [
      {
        path: 'messageRecord',
        redirect: '/smsRecord/messageRecord/sendRecord',
        component: () => import('@/views/smsRecord/messageRecord'),
        meta: { title: '短信收发记录' },
        children: [
          {
            path: 'sendRecord',
            component: () => import('@/views/smsRecord/messageRecord/sendRecord'),
            meta: {
              hide: true,
              title: '发送记录',
              uid: '/smsRecord/messageRecord',
            },
          },
          {
            path: 'receiveRecord',
            component: () => import('@/views/smsRecord/messageRecord/receiveRecord'),
            meta: {
              hide: true,
              title: '接收记录',
              uid: '/smsRecord/messageRecord',
            },
          },
        ],
      },
    ],
  },
  {
    path: '/ipadComputer',
    component: EleLayout,
    hidden: true,
    children: [
      {
        path: 'secondTag/:recId(\\d+)',
        component: () => import('@/views/ipadComputer/secondTag'),
        name: 'AuthUser',
        meta: { title: '评价指标小类', activeMenu: '/ipadComputer' },
      },
    ],
  },
];

/** 404 路由配置 - 需要在动态路由注册后再追加，确保优先级最低 */
const route404 = {
  path: '',
  component: EleLayout,
  meta: { hide: true },
  children: [
    {
      path: '*',
      component: () => import('@/views/common/exception/404'),
      meta: { hide: true, title: '404' },
    },
  ],
};

/** 创建路由实例（使用 history 模式，去掉 mode 配置则回退为 hash 模式） */
const router = new VueRouter({
  routes,
  mode: 'history',
});

/**
 * 全局前置路由守卫
 * 1. 设置页面标题
 * 2. 检查登录状态（token）
 * 3. 未加载菜单时动态注册路由
 * 4. 未登录且不在白名单中则跳转登录页
 */
router.beforeEach((to, from, next) => {
  NProgress.start();
  // 设置页面标题
  document.title = `${to.meta.title || ''}${to.meta.title ? ' - ' : ''}${setting.name}`;
  if (store.state.user.token) {
    // 已登录：检查是否已加载动态路由
    if (!store.state.user.menus) {
      store
        .dispatch('user/getMenuRouters')
        .then((route) => {
          if (route && route.children) {
            route.component = EleLayout;
            // 过滤掉已被静态路由注册的子路由，避免重复注册
            for (let i = route.children.length - 1; i >= 0; i--) {
              if (router.resolve(route.children[i].path).resolved.matched.length) {
                route.children.splice(i, 1);
              }
            }
            // 动态注册菜单路由 + 404 路由
            router.addRoutes([route, route404]);
          }
          // replace: true 确保导航历史中不会留下空白记录
          next({ ...to, replace: true });
        })
        .catch(() => {});
    } else {
      // 动态路由已加载，直接放行
      next();
    }
  } else if (setting.whiteList.indexOf(to.path) !== -1) {
    // 未登录但在白名单内，直接放行
    next();
  } else {
    // 未登录且不在白名单内，跳转登录页
    next({ path: '/login', query: to.path === '/' ? {} : { from: to.path } });
  }
});

/**
 * 全局后置路由钩子
 * 路由切换完成后延迟关闭顶部进度条
 */
router.afterEach(() => {
  setTimeout(() => {
    NProgress.done();
  }, 150);
});

export default router;
